import { PHASE, PLAYER } from "../../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import type { UpdateEvents, Player, ViewInput } from "../../../common/types.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";

type DraftScoutingSeasonContext = "history" | "planning";

const getSeason = async (
	playersAll: Player[],
	season: number,
	seasonContext: DraftScoutingSeasonContext,
) => {
	const playersAllFiltered = playersAll.filter((p) => p.draft.year === season);
	const players = await idb.getCopies.playersPlus(playersAllFiltered, {
		attrs: [
			"pid",
			"firstName",
			"lastName",
			"age",
			"valueFuzz",
			"watch",
			"born",
			"draft",
			"tid",
		],
		ratings: ["ovr", "pot", "skills", "fuzz", "pos", "season"],
		stats: ["ws"],
		showNoStats: true,
		showRookies: true,
		fuzz: true,
	});
	players.sort((a, b) => b.valueFuzz - a.valueFuzz);

	const players2 = addFirstNameShort(
		players.map((pa, i) => {
			const latestRating = pa.ratings.at(-1)!;
			const draftRating =
				pa.ratings.find(
					(rating: (typeof pa.ratings)[number]) => rating.season === season,
				) ??
				pa.ratings[0] ??
				latestRating;

			return {
				actualOvr: seasonContext === "history" ? latestRating.ovr : undefined,
				actualPot: seasonContext === "history" ? latestRating.pot : undefined,
				careerWs:
					seasonContext === "history" ? (pa.careerStats.ws ?? 0) : undefined,
				draftPick:
					seasonContext === "history" && pa.draft.round > 0
						? pa.draft.pick
						: undefined,
				draftRound:
					seasonContext === "history" && pa.draft.round > 0
						? pa.draft.round
						: undefined,
				pid: pa.pid,
				firstName: pa.firstName,
				lastName: pa.lastName,
				age: seasonContext === "history" ? season - pa.born.year : pa.age,
				watch: pa.watch,
				valueFuzz: pa.valueFuzz,
				ovr: draftRating.ovr,
				pot: draftRating.pot,
				skills: draftRating.skills,
				pos: draftRating.pos,
				rank: i + 1,
			};
		}),
	);

	return {
		players: players2,
		season,
	};
};

const updateDraftScouting = async (
	inputs: ViewInput<"draftScouting">,
	updateEvents: UpdateEvents,
	state: {
		requestedSeason?: number;
		seasonContext?: DraftScoutingSeasonContext;
	} = {},
) => {
	const fantasyDraft = g.get("phase") === PHASE.FANTASY_DRAFT;

	// In fantasy draft, use temp tid
	const tid = fantasyDraft ? PLAYER.UNDRAFTED_FANTASY_TEMP : PLAYER.UNDRAFTED;

	// Once a new draft class is generated, if the next season hasn't started, need to bump up year numbers
	const seasonOffset = g.get("phase") >= PHASE.RESIGN_PLAYERS ? 1 : 0;

	const firstSeason = g.get("season") + seasonOffset;
	const requestedSeason = inputs.season;
	const seasonContext =
		requestedSeason !== undefined && requestedSeason < firstSeason
			? "history"
			: "planning";

	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("playerMovement") ||
		requestedSeason !== state.requestedSeason ||
		seasonContext !== state.seasonContext
	) {
		const players =
			seasonContext === "history"
				? await idb.getCopies.players(
						{
							draftYear: requestedSeason,
						},
						"noCopyCache",
					)
				: (
						await idb.cache.players.indexGetAll("playersByDraftYearRetiredYear", [
							[requestedSeason ?? firstSeason],
							[Infinity, Infinity],
						])
					).filter((p) => p.tid === tid);

		let maxDraftYear = requestedSeason ?? firstSeason + 2;
		for (const p of players) {
			if (p.draft.year > maxDraftYear) {
				maxDraftYear = p.draft.year;
			}
		}

		const seasons: Awaited<ReturnType<typeof getSeason>>[] = [];
		if (requestedSeason !== undefined) {
			seasons.push(await getSeason(players, requestedSeason, seasonContext));
		} else {
			for (let season = firstSeason; season <= maxDraftYear; season++) {
				seasons.push(await getSeason(players, season, seasonContext));
			}
		}

		return {
			challengeNoRatings: g.get("challengeNoRatings"),
			currentSeason: g.get("season"),
			draftType: g.get("draftType"),
			firstPlanningSeason: firstSeason,
			fantasyDraft,
			requestedSeason,
			seasonContext,
			seasons,
		};
	}
};

export default updateDraftScouting;
